/**
 * Password utility for administrative functions
 */

const ADMIN_PASSWORD = '3393083';

/**
 * Verify admin password
 * @param {string} password - Password to verify
 * @returns {Promise<boolean>} - True if password is correct
 */
export async function verifyAdminPassword(password) {
    return password === ADMIN_PASSWORD;
}

/**
 * Show password prompt modal
 * @param {string} title - Modal title
 * @param {string} message - Modal message
 * @returns {Promise<string>} - Entered password
 */
export async function showPasswordPrompt(title = 'Authentication Required', message = 'Enter administrator password') {
    const Swal = (await import('sweetalert2')).default;

    return Swal.fire({
        title: title,
        text: message,
        input: 'password',
        inputAttributes: {
            autocapitalize: 'off',
            autocomplete: 'current-password',
            autocorrect: 'off',
            spellcheck: 'false'
        },
        showCancelButton: true,
        showCloseButton: true,
        confirmButtonText: 'Verify',
        cancelButtonText: 'Cancel',
        confirmButtonColor: 'var(--accent-electric-blue)',
        cancelButtonColor: 'var(--text-secondary)',
        background: 'var(--card-bg)',
        color: 'var(--text-primary)',
        customClass: {
            popup: 'password-prompt-popup',
            title: 'password-prompt-title',
            input: 'password-prompt-input',
            confirmButton: 'password-prompt-confirm',
            cancelButton: 'password-prompt-cancel'
        },
        preConfirm: async (inputValue) => {
            if (!inputValue) {
                Swal.showValidationMessage('Password is required');
                return false;
            }

            const isValid = await verifyAdminPassword(inputValue);
            if (!isValid) {
                Swal.showValidationMessage('Incorrect password');
                return false;
            }

            return inputValue;
        },
        willOpen: () => {
            // Apply custom styling
            const popup = document.querySelector('.password-prompt-popup');
            if (popup) {
                popup.style.borderRadius = '12px';
                popup.style.border = 'var(--card-border)';
            }
        }
    }).then((result) => {
        if (result.isConfirmed) {
            return result.value;
        }
        return null;
    });
}

/**
 * Check password asynchronously
 * @param {string} password - Password to check
 * @returns {Promise<{valid: boolean, error?: string}>}
 */
export async function checkPassword(password) {
    if (!password) {
        return { valid: false, error: 'Password is required' };
    }

    const isValid = await verifyAdminPassword(password);
    if (!isValid) {
        return { valid: false, error: 'Incorrect password' };
    }

    return { valid: true };
}
